import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography, CircularProgress } from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import useCommonStyles from "./style";
import {
  getLatestStrapiReportRequest,
  getChatbotReportsRequest,
} from "../../../PatientApiActions";

// Severity colours: green = ok, red = high, amber = concern.
const OK = "#0a8f54";
const HIGH = "#c0392b";
const CONCERN = "#d9772e";
const NEUTRAL = "#6b7280";

// Classify a free-text status/probability into a severity + colour.
const severity = (status) => {
  const s = (status || "").toLowerCase();
  if (
    /(normal|optimal|balanced|good|healthy|in range|low risk|pravara|nirama|no risk)/.test(
      s
    )
  )
    return { concern: false, color: OK };
  if (/(high risk|critical|severe|very high|marked|significant)/.test(s))
    return { concern: true, color: HIGH };
  if (
    /(low|high|elevated|deficien|abnormal|border|risk|attention|aggrav|kshaya|imbalance|mild|moderate|reduced|excess)/.test(
      s
    )
  )
    return { concern: true, color: CONCERN };
  return { concern: false, color: NEUTRAL };
};

const Badge = ({ classes, text, color }) =>
  text ? (
    <span className={classes.hlBadge} style={{ backgroundColor: color }}>
      {text}
    </span>
  ) : null;

const ConcernRow = ({ classes, color, title, sub, tag }) => (
  <Box className={classes.hlConcernRow}>
    <WarningRoundedIcon className={classes.hlRowIcon} style={{ color }} />
    <Box className={classes.hlRowMain}>
      <Typography className={classes.hlRowTitle}>{title}</Typography>
      {sub && <Typography className={classes.hlRowSub}>{sub}</Typography>}
    </Box>
    {tag && (
      <Typography className={classes.hlRowTag} style={{ color }}>
        {tag}
      </Typography>
    )}
  </Box>
);

const Block = ({ classes, title, children }) => (
  <Box>
    <Typography className={classes.hlBlockTitle}>{title}</Typography>
    {children}
  </Box>
);

const Chips = ({ classes, items, concern = true }) => {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  if (!list.length) return null;
  return (
    <Box className={classes.hlChipWrap}>
      {list.map((item, i) => (
        <span
          key={i}
          className={concern ? classes.hlChip : classes.hlChipNeutral}
        >
          {item}
        </span>
      ))}
    </Box>
  );
};

// Per-report renderers — each surfaces that report's deranged / priority data.

// Blood: priority risks, deranged lab markers, at-risk wellness indices.
const BloodHighlights = ({ classes, r }) => {
  const risks = (Array.isArray(r.predictiveRisk) ? r.predictiveRisk : []).filter(
    (x) => x && severity(x.probability).concern
  );
  const flagged = (Array.isArray(r.labFindings) ? r.labFindings : []).filter(
    (x) => x && severity(x.status).concern
  );
  const indices = (
    Array.isArray(r.compositeIndices) ? r.compositeIndices : []
  ).filter((x) => x && severity(x.status).concern);

  return (
    <>
      {r.integratedInsight && (
        <Typography className={classes.hlSummary}>
          {r.integratedInsight}
        </Typography>
      )}

      {risks.length > 0 && (
        <Block classes={classes} title="Priority Risks">
          {risks.map((x, i) => (
            <ConcernRow
              key={i}
              classes={classes}
              color={severity(x.probability).color}
              title={x.area}
              sub={x.interpretation}
              tag={x.probability ? `${x.probability} risk` : ""}
            />
          ))}
        </Block>
      )}

      {flagged.length > 0 && (
        <Block classes={classes} title="Deranged Lab Values">
          {flagged.map((x, i) => {
            const color = severity(x.status).color;
            const value = [x.yourValue, x.idealRange && `(ideal ${x.idealRange})`]
              .filter(Boolean)
              .join(" ");
            return (
              <ConcernRow
                key={i}
                classes={classes}
                color={color}
                title={`${x.test}${value ? ` — ${value}` : ""}`}
                sub={x.meaning || x.ayurvedicCorrelation}
                tag={x.status}
              />
            );
          })}
        </Block>
      )}

      {indices.length > 0 && (
        <Block classes={classes} title="Wellness Indices at Risk">
          {indices.map((x, i) => (
            <ConcernRow
              key={i}
              classes={classes}
              color={severity(x.status).color}
              title={`${x.index}${x.score != null ? ` — ${x.score}/100` : ""}`}
              sub={x.interpretation}
              tag={x.status}
            />
          ))}
        </Block>
      )}

      {!risks.length && !flagged.length && !indices.length && (
        <Typography className={classes.hlSummary}>
          No deranged markers flagged — all measured values are within range.
        </Typography>
      )}
    </>
  );
};

// Tongue: status/score, key observations, likely causes.
const TongueHighlights = ({ classes, r }) => {
  const sev = severity(r.status);
  return (
    <>
      <Box className={classes.hlHeaderRow}>
        <Badge classes={classes} text={r.status} color={sev.color} />
        {r.score && (
          <Typography className={classes.hlHeadline}>Score: {r.score}</Typography>
        )}
      </Box>

      {r.summary && (
        <Typography className={classes.hlSummary}>{r.summary}</Typography>
      )}

      {Array.isArray(r.observations) && r.observations.length > 0 && (
        <Block classes={classes} title="Key Observations">
          {r.observations
            .filter((o) => o && (o.label || o.value))
            .map((o, i) => (
              <Box key={i} className={classes.hlKvRow}>
                <Typography className={classes.hlKvLabel}>{o.label}</Typography>
                <Typography className={classes.hlKvValue}>{o.value}</Typography>
              </Box>
            ))}
        </Block>
      )}

      {Array.isArray(r.possibleCauses) && r.possibleCauses.length > 0 && (
        <Block classes={classes} title="Possible Causes">
          <Chips classes={classes} items={r.possibleCauses} />
        </Block>
      )}
    </>
  );
};

// Nadi: pulse headline, dosha balance (elevated/low), wellness concerns.
const NadiHighlights = ({ classes, r }) => {
  const rows = Array.isArray(r.doshaRows) ? r.doshaRows : [];
  // dosha name -> [status, description], for the label beside each bar.
  const statusFor = {};
  rows.forEach((row) => {
    if (Array.isArray(row) && row[0]) statusFor[String(row[0]).toLowerCase()] = row;
  });
  const doshas = [
    { key: "vata", label: "Vata", value: r.vata },
    { key: "pitta", label: "Pitta", value: r.pitta },
    { key: "kapha", label: "Kapha", value: r.kapha },
  ].filter((d) => d.value != null);

  return (
    <>
      {r.headline && (
        <Typography className={classes.hlHeadline}>{r.headline}</Typography>
      )}

      {doshas.length > 0 && (
        <Block classes={classes} title="Dosha Balance">
          {doshas.map((d) => {
            const row = statusFor[d.key];
            const statusLabel = row && row[1];
            const color = statusLabel ? severity(statusLabel).color : NEUTRAL;
            const pct = Math.max(0, Math.min(100, Number(d.value) || 0));
            return (
              <Box key={d.key} className={classes.hlDoshaRow}>
                <Typography className={classes.hlDoshaLabel}>{d.label}</Typography>
                <Box className={classes.hlDoshaTrack}>
                  <Box
                    className={classes.hlDoshaFill}
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </Box>
                <Typography className={classes.hlDoshaVal} style={{ color }}>
                  {d.value}% {statusLabel ? `· ${statusLabel}` : ""}
                </Typography>
              </Box>
            );
          })}
        </Block>
      )}

      {Array.isArray(r.wellnessTendencies) && r.wellnessTendencies.length > 0 && (
        <Block classes={classes} title="Wellness Tendencies">
          <Chips classes={classes} items={r.wellnessTendencies} />
        </Block>
      )}
    </>
  );
};

// Prakriti: constitution (dosha), key susceptibilities & future risks.
const PrakritiHighlights = ({ classes, r }) => {
  const dosha = [r.primaryDosha, r.secondaryDosha].filter(Boolean).join(" · ");
  return (
    <>
      <Box className={classes.hlHeaderRow}>
        {dosha && <Badge classes={classes} text={dosha} color="#7e57c2" />}
      </Box>

      {r.constitutionDescription && (
        <Typography className={classes.hlSummary}>
          {r.constitutionDescription}
        </Typography>
      )}

      {Array.isArray(r.susceptibility) && r.susceptibility.length > 0 && (
        <Block classes={classes} title="Key Susceptibilities">
          {r.susceptibility
            .filter((s) => Array.isArray(s) && s.length)
            .map((s, i) => (
              <Box key={i} className={classes.hlKvRow}>
                <Typography className={classes.hlKvLabel}>{s[0]}</Typography>
                <Typography className={classes.hlKvValue}>{s[1]}</Typography>
              </Box>
            ))}
        </Block>
      )}

      {Array.isArray(r.futureRisks) && r.futureRisks.length > 0 && (
        <Block classes={classes} title="Future Risks">
          <Chips classes={classes} items={r.futureRisks} />
        </Block>
      )}
    </>
  );
};

// Symptom Analyzer (chatbot): complaint, doshic imbalance, reported symptoms,
// clinical context.
const SymptomHighlights = ({ classes, r }) => {
  const cs = r.clinical_summary || {};
  const ai = r.ayurveda_interpretation || {};
  const doshas = Array.isArray(ai.doshic_pattern) ? ai.doshic_pattern : [];
  const bullets = (Array.isArray(cs.bullets) ? cs.bullets : []).filter(
    (b) => b && b.trim()
  );

  return (
    <>
      {cs.primary_signal && (
        <Typography className={classes.hlHeadline}>
          {cs.primary_signal}
        </Typography>
      )}

      {(cs.pattern || cs.relief) && (
        <Block classes={classes} title="Complaint Details">
          {cs.pattern && (
            <Box className={classes.hlKvRow}>
              <Typography className={classes.hlKvLabel}>Pattern</Typography>
              <Typography className={classes.hlKvValue}>{cs.pattern}</Typography>
            </Box>
          )}
          {cs.relief && (
            <Box className={classes.hlKvRow}>
              <Typography className={classes.hlKvLabel}>Relief</Typography>
              <Typography className={classes.hlKvValue}>{cs.relief}</Typography>
            </Box>
          )}
        </Block>
      )}

      {bullets.length > 0 && (
        <Block classes={classes} title="Reported Symptoms">
          <Chips classes={classes} items={bullets} concern={false} />
        </Block>
      )}

      {doshas.length > 0 && (
        <Block classes={classes} title="Doshic Pattern">
          {doshas
            .filter((d) => d && d.dosha)
            .map((d, i) => (
              <ConcernRow
                key={i}
                classes={classes}
                color={CONCERN}
                title={d.dosha}
                sub={d.role}
              />
            ))}
        </Block>
      )}

      {cs.modern_medical_context && (
        <Block classes={classes} title="Clinical Context">
          <Typography className={classes.hlSummary}>
            {cs.modern_medical_context}
          </Typography>
        </Block>
      )}
    </>
  );
};

const RENDERERS = {
  "clinical-blood-reports": BloodHighlights,
  "tongue-reports": TongueHighlights,
  "nadi-reports": NadiHighlights,
  "prakriti-reports": PrakritiHighlights,
};

const CHATBOT_RENDERERS = {
  symptom_analyzer: SymptomHighlights,
};

// Priority highlights shown above the full report PDF. Source is either a Strapi
// `collection` (blood/tongue/nadi/prakriti) or a `chatbotType` (symptom_analyzer).
const ReportHighlights = ({ phone, collection, chatbotType }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("loading"); // loading | ready | empty
  const [record, setRecord] = useState(null);

  useEffect(() => {
    if (!phone || (!collection && !chatbotType)) {
      setStatus("empty");
      return;
    }
    let active = true;
    setStatus("loading");
    const request = chatbotType
      ? dispatch(getChatbotReportsRequest(phone)).then((reports) => {
          const picked = (Array.isArray(reports) ? reports : [])
            .filter((x) => x && x.report_type === chatbotType && x.payload)
            .sort((a, b) =>
              String(b.report_date || "").localeCompare(
                String(a.report_date || "")
              )
            )[0];
          return picked ? picked.payload : null;
        })
      : dispatch(getLatestStrapiReportRequest(collection, phone));
    request
      .then((rec) => {
        if (!active) return;
        setRecord(rec);
        setStatus(rec ? "ready" : "empty");
      })
      .catch(() => active && setStatus("empty"));
    return () => {
      active = false;
    };
  }, [dispatch, phone, collection, chatbotType]);

  const classes = useCommonStyles();
  const Renderer = collection
    ? RENDERERS[collection]
    : CHATBOT_RENDERERS[chatbotType];

  if (status === "loading") {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (status === "empty" || !record || !Renderer) {
    return (
      <Box className={classes.hiPlaceholder}>
        <InfoOutlinedIcon className={classes.hiPlaceholderIcon} />
        <Typography className={classes.hiPlaceholderNote}>
          No highlighted findings available yet — open the full report below.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.hlWrap}>
      <Renderer classes={classes} r={record} />
    </Box>
  );
};

export default ReportHighlights;
